import { ReactNode, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { removeAlert } from '../../redux/features/alerts/alertsSlice'
import { Alert, AlertColor, AlertTitle, Box, useTheme } from '@mui/material'
import { CheckCircleOutline } from '@mui/icons-material'

type AlertIcon = 'Check';

export type AppAlertJSON = {
  id: number
  severity: AlertColor
  message: string
  title?: string
  icon?: AlertIcon
  dismissable: boolean
}

export class AppAlert {
  public id: number
  public title?: string
  public icon?: AlertIcon = undefined

  constructor(
    public color: AlertColor,
    public message: string,
    public dismissable: boolean,
  ) {
    const now = new Date()
    this.id = now.getTime()
  }

  withTitle = (title: string): AppAlert => {
    this.title = title
    return this
  }

  withIcon = (icon: AlertIcon): AppAlert => {
    this.icon = icon
    return this
  }

  toJSON = (): AppAlertJSON => {
    return {
      id: this.id,
      severity: this.color,
      message: this.message,
      title: this.title,
      icon: this.icon,
      dismissable: this.dismissable,
    }
  }
}

export const AppAlerts = () => {
  const alerts = useAppSelector(state => state.alerts.alerts)
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const [filterOutIDs, setFilterOutIDs] = useState<number[]>([])

  const activeAlerts = alerts.filter(x => !filterOutIDs.includes(x.id))

  const dispatchRemoveAlert = (x: number) => {
    dispatch(removeAlert(x))
    setFilterOutIDs(filterOutIDs.filter(y => y !== x))
  }

  const getIconFromString = (x: AlertIcon | undefined): ReactNode | undefined => {
    if (x === 'Check') {
      return <CheckCircleOutline />
    }
    return undefined
  }

  return (
    <Box sx={{ position: 'fixed', bottom: theme.spacing(2), right: theme.spacing(3), zIndex: 1400 }}>
      {activeAlerts.map(a =>
        <Alert key={a.message} icon={getIconFromString(a.icon)} severity={a.severity} onClose={a.dismissable ? (() => dispatchRemoveAlert(a.id)) : undefined}>
          {a.title ? <AlertTitle>{a.title}</AlertTitle> : null}
          <Box>{a.message}</Box>
        </Alert>)
      }
    </Box>
  )
}